<template>
  <div class="agent-config-detail-container">
    <div class="content-area">
      <div class="page-header">
        <div class="page-header-row">
          <div class="header-left d-flex align-center">
            <v-btn
              icon
              variant="text"
              class="back-btn mr-2"
              :to="{ name: 'AgentConfigs' }">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              Agent Config Detail
            </h1>
          </div>
          <div class="header-right d-flex align-center">
            <v-btn
              icon
              small
              class="mank-btn-icon mr-2"
              :to="{ name: 'EditAgentConfig', params: { externalId } }">
              <Icon name="edit" variant="solid" size="16" color="default"/>
            </v-btn>
            <v-btn icon small class="mank-btn-icon" @click="showDeleteDialog = true">
              <Icon name="trash" variant="solid" size="16" color="default"/>
            </v-btn>
          </div>
        </div>
      </div>

      <div class="main-content">
        <div v-if="loading" class="py-12 text-center">
          <v-progress-circular indeterminate color="primary" size="40"/>
        </div>
        <div v-else class="detail-card">
          <div class="detail-row">
            <span class="label">ID</span>
            <span class="value">{{ detail.id || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">External ID</span>
            <span class="value">{{ detail.external_id || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Description</span>
            <span class="value">{{ detail.description || '-' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Created At</span>
            <span class="value">{{ formatDate(detail.created_at) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Updated At</span>
            <span class="value">{{ formatDate(detail.updated_at) }}</span>
          </div>
          <div class="detail-row full">
            <span class="label d-block mb-2">Config (YAML)</span>
            <pre class="config-box">{{ detail.config || '' }}</pre>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="showDeleteDialog = false"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import AgentConfigService from '@/services/AgentConfigService';

export default {
  name: 'AgentConfigDetail',
  components: { Icon, DeleteConfirmationModal },
  props: {
    externalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      showDeleteDialog: false,
      detail: {},
    };
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('tr-TR');
    },
    async fetchDetail() {
      this.loading = true;
      try {
        const res = await AgentConfigService.getAgentConfigByExternalId(this.externalId);
        if (res?.success && res.data) {
          this.detail = res.data;
        }
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Config detail could not be loaded.',
        }, { root: true });
      } finally {
        this.loading = false;
      }
    },
    async confirmDelete() {
      try {
        await AgentConfigService.deleteAgentConfigByExternalId(this.externalId);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Agent config deleted.',
        }, { root: true });
        this.$router.push({ name: 'AgentConfigs' });
      } catch (e) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: e?.message || 'Delete failed.',
        }, { root: true });
      } finally {
        this.showDeleteDialog = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/colors.scss';

.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.main-content {
  padding: 24px;
  overflow-y: auto;
}

.detail-card {
  background: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: 12px;
  padding: 20px;
}

.detail-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.label {
  font-size: $body-3-size;
  color: $light-foreground-4;
  margin-bottom: 4px;
}

.value {
  font-size: $body-3-size;
  color: $light-foreground-1;
  font-weight: 600;
}

.config-box {
  margin: 0;
  background: $light-surface-1;
  border: 1px solid $light-border;
  border-radius: 8px;
  padding: 12px;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}
</style>
